import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { TOOLS } from "../tools/registry";
import { SignedIn, SignedOut, SignInButton, SignUpButton, UserButton } from "@clerk/clerk-react";
import { LogoHorizontal } from "./brand/Logo";

// The announcement bar was removed: it linked to a "release note" that does not
// exist, and a dismissable promo strip is the most generic SaaS element on the
// page - wrong register for a consultancy selling a A$990 audit.
//
// The link row used to render at every width with no responsive handling, so on
// a 375px screen "Pricing" was clipped mid-word and "My Account" sat off-screen
// entirely - the nav was unusable on a phone. Below `md` the links now collapse
// into a panel behind a menu button; at `md` and up the row is unchanged.

const LINKS = [
  { href: "/#product", label: "Product", testId: "nav-product" },
  { href: "/#briefing", label: "Briefing", testId: "nav-briefing" },
  { href: "/#pricing", label: "Pricing", testId: "nav-pricing" },
];

export default function Nav() {
  const [open, setOpen] = useState(false);
  const [tools, setTools] = useState(false);

  // A hash link inside the panel scrolls the page behind it, so the panel has to
  // close itself or it covers the thing it just navigated to.
  const close = () => { setOpen(false); setTools(false); };

  // Escape closes it, matching every other dismissable surface on the web.
  useEffect(() => {
    if (!open && !tools) return;
    const onKey = (e) => { if (e.key === "Escape") { setOpen(false); setTools(false); } };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, tools]);

  return (
    <div className="fixed top-0 inset-x-0 z-40">
      <header
        data-testid="site-nav"
        className="backdrop-blur-md bg-[#e8e4df]/85"
      >
        <div className="flex items-center justify-between px-5 py-4 md:px-8 md:py-6 max-w-7xl w-full mx-auto">
          <Link
            to="/"
            data-testid="brand-link"
            aria-label="Margin Lens — home"
            className="group shrink-0"
            onClick={close}
          >
            <LogoHorizontal size={26} interactive className="md:hidden" />
            <LogoHorizontal size={30} interactive className="hidden md:inline-flex" />
          </Link>

          {/* Desktop row - unchanged behaviour, now gated to md and up */}
          <nav className="hidden md:flex items-center space-x-8 text-sm font-mono-custom tracking-tight text-neutral-700">
            {LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                className="hover:text-black transition-colors"
                data-testid={l.testId}
              >
                {l.label}
              </a>
            ))}
            {/* Tools. A dropdown rather than a flat row: eight calculators
                would push every other link off a laptop width, and they are a
                section of the site rather than eight peers of "Pricing". */}
            <div
              className="relative"
              onMouseEnter={() => setTools(true)}
              onMouseLeave={() => setTools(false)}
            >
              <button
                type="button"
                onClick={() => setTools((v) => !v)}
                aria-expanded={tools}
                aria-haspopup="true"
                data-testid="nav-tools"
                className="hover:text-black transition-colors inline-flex items-center gap-1"
              >
                Tools
                <svg className="w-3 h-3" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M3 4.5L6 7.5L9 4.5" strokeLinecap="round" />
                </svg>
              </button>
              {tools && (
                <div
                  data-testid="nav-tools-menu"
                  className="absolute right-0 top-full pt-3 w-[19rem]"
                >
                  <div className="border border-[#c9c3b8] bg-[#f5f2ed] shadow-lg shadow-black/5 py-2">
                    {TOOLS.map((t) => (
                      <Link
                        key={t.slug}
                        to={`/tools/${t.slug}`}
                        onClick={() => setTools(false)}
                        data-testid={`nav-tool-${t.slug}`}
                        className="block px-4 py-2 hover:bg-black/5 transition-colors"
                      >
                        <span className="block text-[13px] text-[#171717]">{t.nav}</span>
                        <span className="block text-[11px] text-[#525252] leading-snug">{t.tagline}</span>
                      </Link>
                    ))}
                    <Link
                      to="/tools"
                      onClick={() => setTools(false)}
                      data-testid="nav-tools-all"
                      className="block px-4 pt-2 mt-1 border-t border-[#ddd8ce] text-[12px] text-[#9a3412] hover:text-[#431407] transition-colors"
                    >
                      All calculators
                    </Link>
                  </div>
                </div>
              )}
            </div>
            {/* The Method link is removed while /method is held back for review.
                See the note on the unregistered route in App.js. */}
            {/* Signed out, the nav has to name the ACTION, not the
                destination: "My Account" describes a place a first-time
                visitor does not have. Sign up carries the emphasis because a
                free sweep needs an account, so it is the acquisition path. */}
            <SignedOut>
              <SignInButton mode="modal">
                <button className="hover:text-black transition-colors" data-testid="nav-account-signin">
                  Sign in
                </button>
              </SignInButton>
              <SignUpButton mode="modal">
                <button
                  data-testid="nav-account-signup"
                  className="bg-[#171717] text-[#f5f2ed] px-4 py-1.5 hover:bg-[#431407] transition-colors"
                >
                  Sign up
                </button>
              </SignUpButton>
            </SignedOut>
            <SignedIn>
              <Link to="/account" className="hover:text-black transition-colors" data-testid="nav-account-dashboard">
                My Account
              </Link>
              <UserButton afterSignOutUrl="/" />
            </SignedIn>
          </nav>

          {/* Mobile trigger. The signed-in avatar sits outside the panel so a
              signed-in user can reach their account in one tap, not two. */}
          <div className="flex items-center gap-3 md:hidden">
            <SignedIn>
              <UserButton afterSignOutUrl="/" />
            </SignedIn>
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              aria-expanded={open}
              aria-controls="mobile-nav-panel"
              aria-label={open ? "Close menu" : "Open menu"}
              data-testid="nav-mobile-toggle"
              className="inline-flex items-center justify-center w-10 h-10 -mr-2 rounded-full text-neutral-800 hover:bg-black/5 active:bg-black/10 transition-colors"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round">
                {open ? (
                  <>
                    <path d="M6 6l12 12" />
                    <path d="M18 6L6 18" />
                  </>
                ) : (
                  <>
                    <path d="M3.5 7.5h17" />
                    <path d="M3.5 12h17" />
                    <path d="M3.5 16.5h17" />
                  </>
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile panel */}
        {open && (
          <nav
            id="mobile-nav-panel"
            data-testid="nav-mobile-panel"
            className="md:hidden border-t border-black/10 bg-[#e8e4df]/95 backdrop-blur-md px-5 pb-5 pt-2"
          >
            <ul className="flex flex-col text-[15px] font-mono-custom text-neutral-700">
              {LINKS.map((l) => (
                <li key={l.href}>
                  <a
                    href={l.href}
                    onClick={close}
                    data-testid={`${l.testId}-mobile`}
                    className="block py-3 border-b border-black/5 hover:text-black transition-colors"
                  >
                    {l.label}
                  </a>
                </li>
              ))}
              <li className="py-3 border-b border-black/5">
                <Link
                  to="/tools"
                  onClick={close}
                  data-testid="nav-tools-mobile"
                  className="block text-[#171717] mb-2"
                >
                  Tools
                </Link>
                <ul className="pl-3 border-l border-black/10 space-y-1.5">
                  {TOOLS.map((t) => (
                    <li key={t.slug}>
                      <Link
                        to={`/tools/${t.slug}`}
                        onClick={close}
                        data-testid={`nav-tool-${t.slug}-mobile`}
                        className="block text-[13.5px] hover:text-black transition-colors"
                      >
                        {t.nav}
                      </Link>
                    </li>
                  ))}
                </ul>
              </li>
              <li>
                <SignedOut>
                  <SignInButton mode="modal">
                    <button
                      onClick={close}
                      data-testid="nav-account-signin-mobile"
                      className="block w-full text-left py-3 hover:text-black transition-colors"
                    >
                      Sign in
                    </button>
                  </SignInButton>
                  <SignUpButton mode="modal">
                    <button
                      onClick={close}
                      data-testid="nav-account-signup-mobile"
                      className="block w-full text-left py-3 text-[#171717] hover:text-black transition-colors"
                    >
                      Sign up
                    </button>
                  </SignUpButton>
                </SignedOut>
                <SignedIn>
                  <Link
                    to="/account"
                    onClick={close}
                    data-testid="nav-account-dashboard-mobile"
                    className="block py-3 hover:text-black transition-colors"
                  >
                    My Account
                  </Link>
                </SignedIn>
              </li>
            </ul>
          </nav>
        )}
      </header>
    </div>
  );
}
